import { roundOff } from 'helpers/NumberFormatHelper';
import { TaxHelper } from './TaxHelper';
import { StockTransferHelper } from './StockTransferHelper';

export const CartHelper = () => {
  const TaxManager = TaxHelper();
  const StockTransferManager = StockTransferHelper();

  const getItemAmount = (cartItem) => roundOff(cartItem.quantity * cartItem.rate);

  const getItemAmountWithDiscount = (cartItem) => {
    let amount = getItemAmount(cartItem);
    if (cartItem.discount) {
      amount -= (amount * cartItem.discount) / 100;
    }
    return roundOff(amount);
  };

  const getItemAmountWithDiscountAndTaxes = (cartItem) => {
    let amount = getItemAmountWithDiscount(cartItem);
    amount += TaxManager.getItemCess(cartItem);
    amount += TaxManager.getItemTax(cartItem);
    return roundOff(amount);
  };

  const getItemAmountWithDiscountAndTaxesUsingCartItem = (cartItem) => {
    let amount = getItemAmountWithDiscount(cartItem);
    amount += cartItem.cess;
    amount += cartItem.tax;
    return roundOff(amount);
  };

  const getItemDiscountAmount = (cartItem) => {
    return roundOff(((cartItem.rate * cartItem.discount) / 100) * cartItem.quantity);
  };

  const getCartItemData = (updatingItem, updatingCartItem, qty = null, discount = null) => {
    const rate = TaxManager.getItemUnitRate(updatingItem, updatingCartItem);
    const quantity = qty ?? updatingCartItem.quantity;
    const itemDiscount = discount ?? updatingCartItem?.discount ?? 0;
    updatingItem.quantity = quantity;
    updatingItem.discount = itemDiscount;
    updatingItem.rate = rate;
    const cess = TaxManager.getItemCess(updatingItem);
    const tax = TaxManager.getItemTax(updatingItem);
    const discountAmount = getItemDiscountAmount(updatingItem);
    const itemAmount = getItemAmountWithDiscount(updatingItem);
    const payable = getItemAmountWithDiscountAndTaxes(updatingItem);

    return {
      ...updatingCartItem,
      cess,
      tax,
      payable,
      itemAmount,
      quantity,
      rate,
      discount: itemDiscount,
      discountAmount,
    };
  };

  const spreadAndSetBillLevelDiscount = (saleItems, discount) => {
    const items = saleItems.map((item) => ({ ...item }));
    resetBillLevelDiscount(items);
    let remainingDiscount = discount;

    const cartItemGroupedByTaxSlabs = [...Object.entries(groupBy(items, (cartItem) => cartItem.item?.tax ?? 0))].sort(
      (a, b) => b[0] - a[0]
    );

    for (const [, itemGroup] of cartItemGroupedByTaxSlabs) {
      let itemGroupTotal = 0;
      let itemGroupTotalDiscount = 0;

      itemGroup.forEach((cartItem) => {
        const item = cartItem.item;
        if (!item) return;
        cartItem.rate = item.price;
        itemGroupTotal += getItemAmountWithDiscount(cartItem);
      });

      const applyDiscount = remainingDiscount > 0;
      const groupDiscountPercent = applyDiscount ? Math.min((remainingDiscount / itemGroupTotal) * 100, 100) : 0;

      itemGroup.forEach((cartItem) => {
        const item = cartItem.item;
        if (!item) return;
        cartItem.rate = item.price;

        const itemTotal = getItemAmountWithDiscount(cartItem);
        if (applyDiscount) {
          cartItem.billLevelDiscount = (itemTotal * groupDiscountPercent) / 100;
          itemGroupTotalDiscount += cartItem.billLevelDiscount;
          cartItem.price = (itemTotal - cartItem.billLevelDiscount) / cartItem.quantity;
        } else {
          cartItem.billLevelDiscount = 0;
          cartItem.price = item.price;
        }
      });

      remainingDiscount -= itemGroupTotalDiscount;
    }

    return saveCartItems(items);
  };

  const saveCartItems = (cartItems) => {
    cartItems.forEach((cartItem) => {
      const item = cartItem.item;
      if (item) {
        cartItem.rate = TaxManager.getItemUnitRate(item, cartItem);
        cartItem.tax = TaxManager.getItemTax({ ...cartItem, tax: item.tax, cess: item.cess });
        cartItem.cess = TaxManager.getItemCess({ ...cartItem, tax: item.tax, cess: item.cess });
        cartItem.payable = getItemAmountWithDiscountAndTaxesUsingCartItem(cartItem);
      }
    });

    return cartItems;
  };

  const updateItemRateWithProfit = (cartItems, profitPercentage = 0, rateCalculationMode = 'cost') => {
    cartItems.forEach((cartItem) => {
      const item = cartItem.item;
      if (item) {
        cartItem.rate = StockTransferManager.getStockTransferItemUnitRate(item, profitPercentage, rateCalculationMode);
        const itemForTax = {
          ...cartItem,
          tax_percent: item.tax ?? 0,
          cess_percent: item.cess ?? 0,
        };
        cartItem.tax = StockTransferManager.getItemTax(itemForTax);
        cartItem.cess = StockTransferManager.getItemCess(itemForTax);
        cartItem.payable = cartItem.rate * cartItem.quantity + cartItem.tax + cartItem.cess;
      }
      if (cartItem.sub_item && cartItem.sub_item.length > 0) {
        cartItem.sub_item.forEach((subItem) => {
          const subItemTaxPercent = subItem.tax_percent ?? subItem.tax ?? 0;
          const subItemCessPercent = subItem.cess_percent ?? subItem.cess ?? 0;

          let subItemRate = subItem.price || 0;
          if (rateCalculationMode === 'cost') {
            const cost = subItem.cost || 0;
            if (profitPercentage !== 0) {
              const profitAmount = (cost * profitPercentage) / 100;
              subItemRate = roundOff(cost + profitAmount);
            } else {
              subItemRate = cost;
            }
          }
          subItem.rate = subItemRate;

          const tempSubItem = {
            quantity: subItem.quantity,
            rate: subItem.rate,
            tax_percent: subItemTaxPercent,
            cess_percent: subItemCessPercent
          };
          subItem.cess = StockTransferManager.getItemCess(tempSubItem);
          subItem.tax = StockTransferManager.getItemTax(tempSubItem);
          subItem.payable = subItem.rate * subItem.quantity + subItem.tax + subItem.cess;
        });
      }
    });

    return cartItems;
  };

  const resetBillLevelDiscount = (items) => {
    items.forEach((cartItem) => {
      if (cartItem.billLevelDiscount && cartItem.billLevelDiscount !== 0) {
        const item = cartItem.item;
        if (item) {
          cartItem.rate = TaxManager.getItemUnitRate(item);
        }
        cartItem.billLevelDiscount = 0;
      }
    });
  };

  const groupBy = (array, keyFn) => {
    return array.reduce((result, item) => {
      const key = keyFn(item);
      if (!result[key]) result[key] = [];
      result[key].push(item);
      return result;
    }, {});
  };

  return {
    getItemAmount,
    getItemAmountWithDiscount,
    getItemAmountWithDiscountAndTaxes,
    getItemAmountWithDiscountAndTaxesUsingCartItem,
    getItemDiscountAmount,
    getCartItemData,
    spreadAndSetBillLevelDiscount,
    saveCartItems,
    resetBillLevelDiscount,
    groupBy,
    updateItemRateWithProfit,
  };
};
